/**
 * Form building blocks (CF-0030): react-hook-form + zod, server error mapping, sections, dirty guard.
 *
 *   const form = useAppForm(schema, { defaultValues });
 *   <FormShell form={form} onSubmit={save} title="Customer">
 *     <FormSection title="General"><FormText name="name" label="Name" /></FormSection>
 *   </FormShell>
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Box, Button, Card, CardContent, CardHeader, Grid, MenuItem, Stack, TextField, type TextFieldProps } from "@mui/material";
import { useEffect, type ReactNode } from "react";
import { Controller, FormProvider, useForm, useFormContext, type DefaultValues, type FieldValues, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router-dom";
import type { ZodType, ZodTypeDef } from "zod";

import { errorMessage, type ApiError } from "../api/client";

export function useAppForm<T extends FieldValues>(schema: ZodType<T, ZodTypeDef, unknown>, options: { defaultValues?: DefaultValues<T> } = {}) {
  return useForm<T>({ resolver: zodResolver(schema as never), mode: "onBlur", ...options });
}

/** Apply a DRF validation error envelope ({"error": {"details": {field: [msg]}}}) to the form. */
export function applyServerErrors<T extends FieldValues>(form: UseFormReturn<T>, error: unknown): string {
  const details = (error as ApiError | undefined)?.error?.details as Record<string, unknown> | undefined;
  if (details && typeof details === "object") {
    let nonField = "";
    for (const [field, messages] of Object.entries(details)) {
      const message = Array.isArray(messages) ? String(messages[0]) : String(messages);
      if (field === "non_field_errors" || field === "detail") nonField = message;
      else form.setError(field as never, { type: "server", message });
    }
    if (nonField) return nonField;
    return "Please correct the highlighted fields.";
  }
  return errorMessage(error);
}

export function FormShell<T extends FieldValues>({ form, onSubmit, children, title, actions, submitLabel, error, busy }: {
  form: UseFormReturn<T>;
  onSubmit: (values: T) => Promise<void> | void;
  children: ReactNode;
  title?: string;
  actions?: ReactNode;
  submitLabel?: string;
  error?: string | null;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const dirty = form.formState.isDirty && !form.formState.isSubmitSuccessful;
  const blocker = useBlocker(dirty);
  useEffect(() => {
    if (blocker.state === "blocked") {
      if (window.confirm(t("common.unsavedChanges"))) blocker.proceed();
      else blocker.reset();
    }
  }, [blocker, t]);
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => { if (dirty) { e.preventDefault(); } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);
  return (
    <FormProvider {...form}>
      <Box component="form" noValidate onSubmit={form.handleSubmit(onSubmit)} sx={{ display: "grid", gap: 2 }}>
        {title && <CardHeader title={title} sx={{ p: 0 }} />}
        {error && <Alert severity="error">{error}</Alert>}
        {children}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {actions}
          <Button type="submit" variant="contained" disabled={busy || form.formState.isSubmitting}>
            {submitLabel ?? t("common.save")}
          </Button>
        </Stack>
      </Box>
    </FormProvider>
  );
}

export function FormSection({ title, children, columns = 2 }: { title?: string; children: ReactNode; columns?: 1 | 2 | 3 | 4 }) {
  return (
    <Card>
      {title && <CardHeader title={title} slotProps={{ title: { variant: "subtitle2" } }} sx={{ pb: 0 }} />}
      <CardContent>
        <Grid container spacing={2} columns={columns * 12}>
          {Array.isArray(children) ? children.map((c, i) => <Grid key={i} size={{ xs: columns * 12, md: 12 }}>{c}</Grid>) : <Grid size={{ xs: columns * 12, md: 12 }}>{children}</Grid>}
        </Grid>
      </CardContent>
    </Card>
  );
}

type FieldProps = { name: string; label: string; helperText?: string } & Omit<TextFieldProps, "name" | "label" | "error">;

export function FormText({ name, label, helperText, ...rest }: FieldProps) {
  const { control } = useFormContext();
  return (
    <Controller name={name} control={control} render={({ field, fieldState }) => (
      <TextField {...field} value={field.value ?? ""} label={label} error={!!fieldState.error}
        helperText={fieldState.error?.message ?? helperText} {...rest} />
    )} />
  );
}

export function FormSelect({ name, label, options, helperText, ...rest }: FieldProps & { options: { value: string | number; label: string }[] }) {
  const { control } = useFormContext();
  return (
    <Controller name={name} control={control} render={({ field, fieldState }) => (
      <TextField {...field} select value={field.value ?? ""} label={label} error={!!fieldState.error}
        helperText={fieldState.error?.message ?? helperText} {...rest}>
        {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </TextField>
    )} />
  );
}

export function FormNumber(props: FieldProps) {
  return <FormText type="number" inputProps={{ inputMode: "decimal", step: "any" }} {...props} />;
}

export function FormDate(props: FieldProps) {
  return <FormText type="date" InputLabelProps={{ shrink: true }} {...props} />;
}
